export type ShareBudgetMismatch = "budget_without_share" | "share_without_budget";

/**
 * Below a euro, a budget is rounding rather than money — the same slack the
 * budget-split check allows. Impressions get the same: less than one is none.
 */
const THRESHOLD = 1;

/**
 * Where a reservation book's two levers contradict each other on one target,
 * flagged on the target where the contradiction is set rather than on
 * everything that inherits it:
 *
 * - `budget_without_share`: the target reserves 0% in every month, set right
 *   here, yet the book still hands it budget. Nothing is reserved to earn it,
 *   so that budget is lost and the plan lands under its target.
 * - `share_without_budget`: the target's budget is set to nothing right here
 *   (a 0% split or a €0 fixed amount), yet it still reserves impressions —
 *   given away at a CPM of €0.
 *
 * Only meaningful for a reservation book; open inventory has no shares.
 */
export const shareBudgetMismatch = (target: {
	budget: number;
	gross_impressions: number;
	monthly_impression_share: number[];
	impression_share_own: boolean;
	/** Does the budget come from an override on this exact target (a split or a fixed amount)? */
	budget_own: boolean;
}): ShareBudgetMismatch | null => {
	const reservesNothing = Object.values(target.monthly_impression_share).every((share) => share <= 0);
	const hasBudget = target.budget >= THRESHOLD;
	const hasImpressions = target.gross_impressions >= THRESHOLD;

	if (target.impression_share_own && reservesNothing && !hasImpressions && hasBudget) {
		return "budget_without_share";
	}

	if (target.budget_own && !hasBudget && !reservesNothing && hasImpressions) {
		return "share_without_budget";
	}

	return null;
};
